import { useState, createContext, useCallback, useEffect, useRef } from "react";
import useAuth from "../hooks/useAuth";
import useWebotServer from "../hooks/useWebotServer";
import moment from "moment";
import { useModal } from "mui-modal-provider";
import { removeUndefined } from "utils/Haim/firestoreUtils";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "components/__haim/Common/ConfirmationDialog";
import { getUpdatedContactsQuery, postProcessContact } from "utils/contacts/helper";
import {
  mergeIntoBook,
  normaliseBookContact,
  removeFromBook,
  upsertIntoBook
} from "utils/contacts/legacyBook";
import { rekeyContactInList } from "utils/contacts/contactRekey";
import { contactLookupStore } from "hooks/contacts/contactLookupStore";
import { CONTACT_DOCUMENT_SOURCE } from "hooks/contacts/contactDocumentSource";
import { getContactSignalsQuery } from "hooks/contacts/contactSignals";
import useContactBook from "hooks/contacts/useContactBook";
import { pickSummaryFields, rowToContactSummary } from "hooks/contacts/contactSummary";
import { toContactWriteError } from "utils/contacts/contactWriteErrors";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import type { ReactNode } from "react";
import type { RealEstateContact, WholeBookDownloadSource } from "../@types/sharedSchema";
import type { AppContextProps } from "../@types/app";
import type { CrmContactUpdateResponse } from "../@types/webotServer";

const AppContext = createContext<AppContextProps | null>(null);

/** How far before its anchor the live feed's window opens — see the listener in `AppProvider`. */
const LIVE_FEED_CLOCK_SKEW_MS = 2 * 60 * 1000;
/** The most documents one live-feed snapshot can carry. A full catch-up means "re-download". */
const LIVE_FEED_WINDOW = 100;

type AppProviderProps = {
  children: ReactNode;
};

function AppProvider({ children }: AppProviderProps): JSX.Element {
  const [allContacts, setAllContacts] = useState<RealEstateContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [firstContactLoaded, setFirstContactLoaded] = useState(false);
  // Which user's all-contacts fetch has started (sync guard, set before the
  // request goes out). The fetch effect used to have no idempotency guard, so
  // any re-run — a remount, an auth-object rebuild, an effect-dep flicker —
  // fired a second full POST /get-data/all-contacts back-to-back (measured
  // twice per page load in prod, 2.4-4.4s each). One fetch per signed-in user,
  // until `invalidateContactsBook` says the result can no longer be trusted;
  // what keeps the array fresh in between is the bridge below.
  const contactsFetchStartedForRef = useRef<string | null>(null);
  // Which legacy table armed the download — sent with it, so the server's meter
  // (`contacts_whole_book_rows{source}`) can say which screen still needs the endpoint.
  const armedByRef = useRef<WholeBookDownloadSource | null>(null);
  // Settles once the fetch below has run to completion — success OR failure.
  // `allContacts.length === 0` is ambiguous on its own (empty array is both
  // the initial state and a legitimate result), which made contact lookups
  // report "not in your contacts" before the list had even been requested.
  const [contactsLoaded, setContactsLoaded] = useState(false);

  // ── THE BRIDGE: keeping the one-shot book true ───────────────────────────────────
  //
  // "Mutations keep the array fresh locally" stopped being true the day a write moved to a
  // server endpoint: nothing inserted the contact it created, and the old contacts page —
  // which renders this array and nothing else — stopped showing new contacts until a
  // reload (Trello #207). Contacts created by a call analysis or by the AI assistant had
  // never reached it at all.
  //
  // The array now changes in exactly three ways, all defined in `legacyBook.ts`: a document
  // arriving on the live listener below, a write we made ourselves
  // (`upsertContactsInMemory`), and a delete we made (`removeContactsInMemory`). A change
  // we cannot enumerate — a server bulk action over a filter — calls
  // `invalidateContactsBook` and the next legacy render downloads again.

  /**
   * True from the moment a book has landed for this user until sign-out. It STAYS true
   * through an invalidation: the stale book is still what is on screen, and the download
   * that replaces it is a revalidation, not a first load.
   */
  const bookLoadedRef = useRef(false);
  /**
   * Bumped by every fetch start, sign-out and invalidation. A download takes 2–14 s on a
   * big book and nothing cancels it, so one that lands after the realtor signed out used to
   * write the PREVIOUS user's contacts into the next session's state.
   */
  const fetchTokenRef = useRef(0);
  /**
   * Documents the listener delivered while a download was in flight. The download read its
   * snapshot before they changed, so its result would silently overwrite them; they are
   * re-applied on top when it lands.
   */
  const fetchInFlightRef = useRef(false);
  const liveDocsDuringFetchRef = useRef<Map<string, RealEstateContact>>(new Map());
  /** How many legacy tables are on screen and want the book live. See the feed below. */
  const [liveFeedHolders, setLiveFeedHolders] = useState(0);
  /** When the book was last known true (ms) — where the next live-feed window opens. */
  const feedAnchorRef = useRef<number | null>(null);

  const { user, isAuthenticated } = useAuth();

  // The contact EDIT primitive posts to the server, and this is the ONE client for that
  // endpoint — no second `fetch` here. `AppProvider` is mounted inside
  // `WebotServerProvider` (App.tsx), so the context is always populated.
  //
  // Held in a ref because `useWebotServer()`
  // rebuilds its whole context object on every provider render, so the function is not
  // referentially stable, and reading it through a ref keeps `updateExistingContact`
  // from being re-created on unrelated re-renders of the app shell.
  const { contactsUpdate, contactsGetMany } = useWebotServer();
  const contactsUpdateRef = useRef(contactsUpdate);
  contactsUpdateRef.current = contactsUpdate;
  // The BOOK the 'server' feed listens on — the email for a personal book, the office's
  // `org:` key for a member of a pooled agency (plan D4). Strict: null until the server has
  // said, and the signals feed waits for it rather than listen under a book nothing writes
  // to. The 'firestore' feed keeps the email: a pooled record has no document there.
  const { bookKey: liveFeedBook } = useContactBook();
  // The live feed's catch-up in 'server' mode (below) — the same ref pattern, for the
  // same reason.
  const contactsGetManyRef = useRef(contactsGetMany);
  contactsGetManyRef.current = contactsGetMany;

  const { t: translate } = useTranslation();

  const { showModal } = useModal();

  useEffect(() => {
    if (isAuthenticated) return;

    setAllContacts([]);
    // Back to "not loaded" on sign-out, so the next session doesn't read the
    // previous user's settled state over an empty list.
    setContactsLoaded(false);
    setIsLoadingContacts(false);
    contactsFetchStartedForRef.current = null;
    // The ARM goes too. It used to survive sign-out, so the next account to sign in on
    // this tab downloaded its whole book with no legacy table on screen to read it.
    setFirstContactLoaded(false);
    // And whatever download is still in flight belongs to the account that just left.
    fetchTokenRef.current += 1;
    fetchInFlightRef.current = false;
    liveDocsDuringFetchRef.current.clear();
    bookLoadedRef.current = false;
    feedAnchorRef.current = null;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!user?.email) return;
    if (!firstContactLoaded) return;
    if (contactsFetchStartedForRef.current === user.email) return;
    contactsFetchStartedForRef.current = user.email;

    const email = user.email;
    const fetchToken = ++fetchTokenRef.current;
    const isCurrentFetch = (): boolean => fetchToken === fetchTokenRef.current;
    // A book already on screen stays on screen while a fresh one downloads: this branch is
    // reached again after `invalidateContactsBook`, and flipping the loading flag there
    // would blank the legacy tables for the 2–14 s a big book takes.
    const isRevalidation = bookLoadedRef.current;
    fetchInFlightRef.current = true;
    liveDocsDuringFetchRef.current.clear();
    // The download reads its snapshot some time after this instant, so a feed window that
    // opens here misses nothing the download did not carry.
    feedAnchorRef.current = Date.now();

    const fetchContactsFromServer = async (userIdToken: string): Promise<RealEstateContact[]> => {
      const url = `${WEBOT_SERVER_ENDPOINT}/api/v1/get-data/all-contacts`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: userIdToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ source: armedByRef.current })
      });
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      return response.json();
    };

    // NO FALLBACK (2026-10-24). A failed server answer used to fall back to the `getContacts`
    // Cloud Function, which read the book straight out of Firestore — and since contact records
    // moved to Postgres a Firestore contact document is a FROZEN copy: the fallback would have
    // shown the realtor their book as it was on the day of the move, with nothing on screen to
    // say so. A failure now says so instead, and the next load asks the server again.
    const getContacts = async (): Promise<void> => {
      if (!isRevalidation) setIsLoadingContacts(true);

      const userIdToken = await user?.getIdToken();

      let rawContacts: RealEstateContact[];
      try {
        rawContacts = await fetchContactsFromServer(userIdToken);
      } catch (serverErr) {
        // Signed out, or superseded, while it was failing: the next owner of this state
        // must not inherit this fetch's error modal or its settled flags.
        if (!isCurrentFetch()) return;
        console.error("Contacts fetch from the server failed", serverErr);
        fetchInFlightRef.current = false;
        liveDocsDuringFetchRef.current.clear();
        showModal(ConfirmationDialog, {
          title: translate(`contactsPage.error.failedToGetContacts`),
          description: translate("contactsPage.error.tryRefreshingThePage")
        });
        setIsLoadingContacts(false);
        // Settle it even on failure: consumers gate a spinner on this, and
        // the error modal above already tells the realtor what happened —
        // an indefinite spinner on top of that helps nobody.
        setContactsLoaded(true);
        return;
      }

      // Signed out, or superseded, while it was downloading (see `fetchTokenRef`).
      if (!isCurrentFetch()) return;

      let contactsData = rawContacts.map((data: RealEstateContact) =>
        postProcessContact(data, email)
      );

      contactsData = contactsData.sort((a, b) => (a.createdTime < b.createdTime ? 1 : -1));

      // Whatever the listener delivered while this was downloading goes back on top: the
      // download read its snapshot before those documents changed.
      const liveDocs = Array.from(liveDocsDuringFetchRef.current.values());
      liveDocsDuringFetchRef.current.clear();
      fetchInFlightRef.current = false;
      bookLoadedRef.current = true;

      setAllContacts(mergeIntoBook(contactsData, liveDocs));
      setIsLoadingContacts(false);
      setContactsLoaded(true);
    };

    void getContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, firstContactLoaded]);

  /**
   * THE LIVE FEED — every contact that changes while a legacy table is on screen, and
   * everything that changed since one last was.
   *
   * This listener used to live in the old contacts table, where it could only patch rows
   * that were already on screen: a NEW contact arrived, found no row to update, and was
   * dropped. That is the whole of Trello #207 for everything not created in this tab — a
   * call analysis, the AI assistant, the new contacts page, another device. Here it feeds
   * the array itself, so an unknown id becomes a row.
   *
   * ── It runs only while somebody holds it (`retainContactsLiveFeed`) ───────────────
   *
   * Not for as long as the book is armed. Two tables arm it (see `useLegacyContactsBook`)
   * and only one wants it live: the old contacts page, the one surface a realtor reads the
   * book ON. The send wizard's grid does not hold it — rows refreshing under a half-made
   * selection are worse than a list that is a few minutes old — and a feed that ran for it
   * anyway would re-run the grid's whole-book filters on every contact a call analysis
   * touches.
   *
   * ── The window opens where the last one closed ───────────────────────────────────
   *
   * `feedAnchorRef` is when the book was last known true: the start of its download, then
   * every moment the feed was torn down. So a table that comes back on screen is first
   * handed everything that changed while it was away — an edit made on the new contacts
   * page shows up on return, not after a reload.
   *
   * The query carries at most {@link LIVE_FEED_WINDOW} documents. If a CATCH-UP snapshot
   * arrives full, more may have changed than it can carry, and the only honest reaction is
   * to stop trusting the book: it is invalidated and the table downloads it again. (Not
   * during a download — that IS the catch-up, and a bulk import running elsewhere would
   * otherwise keep the window full and re-download in a loop.)
   *
   * Three things about how a snapshot is read, each of which is a bug if changed:
   *
   *  - `docChanges()`, never `snapshot.docs`. Every snapshot carries the whole window;
   *    re-applying it would repaint a hundred unrelated contacts over whatever optimistic
   *    edit is in flight on them.
   *  - `removed` is IGNORED. On a limited query it also means "pushed out of the window by
   *    a newer change", so treating it as a delete would drop live contacts. Deletes reach
   *    the array through `removeContactsInMemory` / `invalidateContactsBook`.
   *  - The window opens two minutes BEFORE its anchor. The anchor is this browser's clock,
   *    and a fast one would otherwise miss everything written until real time caught up.
   *
   * ── Which feed: `CONTACT_DOCUMENT_SOURCE` ────────────────────────────────────────
   *
   * The contact record is moving to Postgres (the server's contacts-postgres-authority
   * plan, D2), where there is no document to query by `updatedTime`. In 'server' mode the
   * same window is read off the book's SIGNAL documents instead (`contact-signals`,
   * `{ v, at, deleted? }`, written by the server after every change) and the documents
   * behind a snapshot's ids come from `POST /contacts/get-many`. The three rules above hold
   * unchanged. It is also more correct: a `deleted` signal removes the row, which the
   * `updatedTime` feed could never see, and a write that carries
   * `skipUpdatedTimeAroundDate` still bumps the version. Until the flip, the `updatedTime`
   * query is the feed.
   */
  useEffect(() => {
    const email = user?.email;
    if (!email || !firstContactLoaded || liveFeedHolders === 0) return undefined;

    const since = new Date((feedAnchorRef.current ?? Date.now()) - LIVE_FEED_CLOCK_SKEW_MS);
    // A book that is loaded and not being downloaded right now: the first snapshot is the
    // catch-up for the time nobody was listening.
    let awaitingCatchUp = bookLoadedRef.current && !fetchInFlightRef.current;

    /** Whole documents that just changed go into the book — and wait for a download in flight. */
    const applyChanged = (changed: RealEstateContact[]): void => {
      if (changed.length === 0) return;
      if (fetchInFlightRef.current) {
        changed.forEach((contact) => liveDocsDuringFetchRef.current.set(contact.id, contact));
      }
      if (bookLoadedRef.current) setAllContacts((current) => mergeIntoBook(current, changed));
    };

    const onError = (error: unknown): void => {
      // The book still works without it — it just goes back to being a snapshot.
      console.error("Contacts live feed failed", error);
    };

    if (CONTACT_DOCUMENT_SOURCE !== "server") {
      const unsubscribe = getUpdatedContactsQuery(email, since, LIVE_FEED_WINDOW).onSnapshot(
        (snapshot) => {
          const isCatchUp = awaitingCatchUp;
          awaitingCatchUp = false;
          if (isCatchUp && snapshot.size >= LIVE_FEED_WINDOW) {
            invalidateContactsBook();
            return;
          }

          applyChanged(
            snapshot
              .docChanges()
              .filter((change) => change.type !== "removed")
              .map((change) => normaliseBookContact(change.doc.data(), email, postProcessContact))
          );
        },
        onError
      );

      return () => {
        unsubscribe();
        // Everything up to this moment was applied; the next window opens here.
        feedAnchorRef.current = Date.now();
      };
    }

    // ── 'server': the signals feed ──────────────────────────────────────────────────
    //
    // A snapshot names contacts; their documents are fetched behind it. The fetches run
    // one after the other, in snapshot order, so an older page can never land over a newer
    // one. When the feed is torn down with fetches still out, the next window opens where
    // the OLDEST unapplied snapshot arrived rather than "now", so nothing fetched late is
    // lost — the documents are simply asked for again on the next catch-up.
    //
    // Keyed on the BOOK, which the server names (`useContactBook`); until it has, there is
    // nothing to listen on — the effect re-runs when the answer lands.
    if (!liveFeedBook) return undefined;
    let queue: Promise<void> = Promise.resolve();
    let cancelled = false;
    let sequence = 0;
    /** Snapshot sequence → when it arrived (ms), for every snapshot whose fetch is still out. */
    const pending = new Map<number, number>();

    /** Contacts whose signal says `deleted`, or that `get-many` reports missing. */
    const removeGone = (ids: string[]): void => {
      if (ids.length === 0) return;
      ids.forEach((id) => liveDocsDuringFetchRef.current.delete(id));
      if (bookLoadedRef.current) setAllContacts((current) => removeFromBook(current, ids));
    };

    const unsubscribe = getContactSignalsQuery(liveFeedBook, since, LIVE_FEED_WINDOW).onSnapshot(
      (snapshot) => {
        const isCatchUp = awaitingCatchUp;
        awaitingCatchUp = false;
        if (isCatchUp && snapshot.size >= LIVE_FEED_WINDOW) {
          invalidateContactsBook();
          return;
        }

        const changes = snapshot.docChanges().filter((change) => change.type !== "removed");
        const isDeleted = (change: typeof changes[number]): boolean => change.doc.data()?.deleted === true;
        removeGone(changes.filter(isDeleted).map((change) => change.doc.id));
        const changedIds = changes.filter((change) => !isDeleted(change)).map((change) => change.doc.id);
        if (changedIds.length === 0) return;

        const mySequence = ++sequence;
        pending.set(mySequence, Date.now());
        queue = queue
          .then(async () => {
            if (cancelled) return;
            const response = await contactsGetManyRef.current({ contactIds: changedIds });
            if (cancelled) return;
            applyChanged(
              response.contacts.map(({ doc }) => normaliseBookContact(doc, email, postProcessContact))
            );
            // Asked about and not there: deleted since its signal.
            removeGone(response.missing);
          })
          .catch(onError)
          .finally(() => pending.delete(mySequence));
      },
      onError
    );

    return () => {
      cancelled = true;
      unsubscribe();
      feedAnchorRef.current = pending.size > 0 ? Math.min(...pending.values()) : Date.now();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, liveFeedBook, firstContactLoaded, liveFeedHolders === 0]);

  /**
   * Keeps the live feed running while the caller is mounted — the old contacts page.
   * Returns the release, so it is used as an effect:
   * `useEffect(() => retainContactsLiveFeed(), [retainContactsLiveFeed])`.
   */
  const retainContactsLiveFeed = useCallback((): (() => void) => {
    setLiveFeedHolders((holders) => holders + 1);
    return () => setLiveFeedHolders((holders) => Math.max(0, holders - 1));
  }, []);

  /**
   * A contact WE just wrote through a server endpoint — put it in the book now.
   *
   * The live feed would deliver it too, but only after the Cloud Functions stamp it, a few
   * seconds later; a realtor who has just pressed "save" is looking at the list NOW.
   *
   * A no-op until a book has landed. With nothing loaded, inserting here would leave the app
   * holding `[thisOneContact]` and believing the realtor owns exactly one contact — and the
   * download, when it comes, carries the contact anyway.
   */
  const upsertContactsInMemory = (contacts: Partial<RealEstateContact>[]): void => {
    const email = user?.email;
    if (!email || !bookLoadedRef.current || contacts.length === 0) return;

    const nowIso = moment().toISOString();
    setAllContacts((current) =>
      upsertIntoBook(current, contacts, nowIso, (partial) =>
        postProcessContact({ ...partial } as RealEstateContact, email)
      )
    );
  };

  /** A contact WE just deleted through a server endpoint. The live feed cannot see a delete. */
  const removeContactsInMemory = (contactIds: string[]): void => {
    if (!bookLoadedRef.current || contactIds.length === 0) return;
    setAllContacts((current) => removeFromBook(current, contactIds));
  };

  /**
   * The book can no longer be trusted, and we cannot say which rows are wrong.
   *
   * For a server bulk action resolved from a FILTER ("all 8,102 matching"): the browser
   * never saw the ids, and the live feed carries at most 100 changes and no deletes.
   *
   * LAZY on purpose — it only disarms. The download happens the next time a legacy table
   * actually renders and calls `getAllContacts()`; a realtor who bulk-deletes on the new
   * contacts page and stays there pays nothing. The stale rows stay on screen meanwhile
   * (and the refetch does not blank them — see `isRevalidation`), but `contactsLoaded` goes
   * false so nothing treats the array as an authoritative "this phone is not a contact".
   */
  const invalidateContactsBook = (): void => {
    if (!bookLoadedRef.current && contactsFetchStartedForRef.current === null) return;
    fetchTokenRef.current += 1;
    fetchInFlightRef.current = false;
    liveDocsDuringFetchRef.current.clear();
    contactsFetchStartedForRef.current = null;
    setIsLoadingContacts(false);
    setContactsLoaded(false);
    setFirstContactLoaded(false);
  };

  const getAllContacts = (source: WholeBookDownloadSource) => {
    if (!firstContactLoaded) {
      armedByRef.current = source;
      setTimeout(() => {
        setFirstContactLoaded(true);
      }, 0);
    }

    return allContacts;
  };

  /**
   * Patches ONE contact in the in-browser array, and hands back the undo.
   *
   * Split out because the patch now happens BEFORE the write rather than after it (see
   * `updateExistingContact`), which is what makes an undo necessary at all — an
   * optimistic edit that fails must not leave the wrong value on screen.
   *
   * What is captured is the PREVIOUS VALUE OF THE PATCHED KEYS, not the array. Snapshotting
   * the whole array would restore a list that may since have been replaced wholesale by
   * the one-shot bulk fetch, or patched by a concurrent edit to a different contact —
   * rolling back one failed field would then silently discard both.
   */
  const patchContactInMemory = (
    contactId: string,
    patch: Record<string, unknown>
  ): (() => void) => {
    // Computed once, outside the updater, so the updater stays a pure function of its
    // input — React re-runs state updaters in StrictMode specifically to catch ones
    // that are not.
    //
    // The cast names a lie this array has always told: `updatedTime` is typed `Date` but
    // every optimistic patch in this file (and the bulk fetch's own sort) puts an ISO
    // STRING there. Preserved rather than corrected, because the consumers read it as a
    // string; fixing the type is a separate change with its own blast radius.
    const stamp = moment().toISOString() as unknown as Date;
    const capturedKeys = [...Object.keys(patch), "updatedTime"];
    let previous: Record<string, unknown> | null = null;

    // Functional update: this can run while the one-shot bulk fetch is in flight, and a
    // closure over `allContacts` would write a stale array back over its result.
    setAllContacts((current) => {
      const target = current.find((contact) => contact.id === contactId) as
        | Record<string, unknown>
        | undefined;
      // The array legitimately does not hold this contact — the CRM page never loads it,
      // and elsewhere it lands asynchronously. Leave it completely alone; the screens
      // that show this contact read it live (see the reconciliation note below).
      if (!target) return current;
      // First invocation wins. StrictMode runs this updater twice with the same input;
      // re-capturing on the second pass would snapshot the values this very call is
      // about to write, i.e. produce an undo that restores the new value.
      if (previous === null) {
        const snapshot: Record<string, unknown> = {};
        capturedKeys.forEach((key) => {
          snapshot[key] = target[key];
        });
        previous = snapshot;
      }
      return current.map((contact) =>
        contact.id === contactId
          ? ({ ...contact, ...patch, updatedTime: stamp } as RealEstateContact)
          : contact
      );
    });

    return () => {
      if (!previous) return; // nothing was patched, so there is nothing to undo
      const restore = previous;
      setAllContacts((current) =>
        current.map((contact) =>
          contact.id === contactId
            ? ({ ...contact, ...restore } as RealEstateContact)
            : contact
        )
      );
    };
  };

  /**
   * The in-memory half of the phone-change (re-key) flow — the array analogue of what
   * `POST /api/v1/contacts/change-phone` already did to the database.
   *
   * Runs AFTER the server succeeded, so unlike `patchContactInMemory` there is no undo
   * to hand back: the old document is hard-deleted, and "restoring" the old id would
   * resurrect a row whose every interaction 404s. Functional update for the same
   * reason as the patch above — the one-shot bulk fetch may be in flight, and a
   * closure over `allContacts` would write a stale array back over its result.
   * `rekeyContactInList` returns the SAME array when the old id is absent (the CRM
   * page never loads this array), so a miss re-renders nothing.
   */
  const rekeyContactInMemory = (oldContactId: string, newContactId: string): void => {
    const email = user?.email;
    if (!email) return;
    setAllContacts((current) => rekeyContactInList(current, oldContactId, newContactId, email));
    // The lookup store is keyed by phone: the old number is nobody now, and what it holds
    // about the NEW one ("not in your book", most likely) stopped being true. Re-asking is
    // exact here — the re-key is a server write, mirrored before it answered.
    contactLookupStore.markAbsent([oldContactId]);
    contactLookupStore.invalidate();
  };

  /**
   * Edit a contact that ALREADY EXISTS in the signed-in user's own book.
   *
   * ── Why this exists ───────────────────────────────────────────────────────────
   *
   * It replaced the browser writer `updateContact` — `set(..., { merge: true })` straight
   * from the browser, gone since 2026-10 with the freeze of the contact document; the two
   * legacy tables' `updateContact` is a wrapper in `useLegacyContactsBook.ts` now. That
   * writer made two assumptions that were true for years and are not true any more, and
   * they are the same mistake twice: it assumed the contact belongs to the signed-in user,
   * and it assumed `allContacts` is loaded.
   *
   *   1. It wrote `set(..., { merge: true })`. On a document that isn't there, that
   *      CREATES one. Handed a colleague's contact — which the CRM page's agency view
   *      can put on screen — it silently minted a phantom in the viewer's own book
   *      carrying whatever single field was being edited. The same applied to a
   *      contact of the viewer's that had since been deleted elsewhere.
   *   2. It resolved the contact's prior state with `allContacts.find(...)`. The CRM
   *      page deliberately never loads that array (25,066 documents for one real
   *      user), and even on pages that do, the array lands ASYNCHRONOUSLY while the
   *      customer page renders from a single live document read — so "not found" was
   *      routinely "not loaded", not "does not exist". `buildCrmStatusUpdate` then
   *      derived the legacy `customerSeriousness` mirror from a null prior state, and
   *      the create branch replaced the whole array with `[theOneContact]`, leaving
   *      the app believing this realtor owns exactly one contact.
   *
   * ── THE WRITE IS NOW SERVER-SIDE ──────────────────────────────────────────────
   *
   * `POST /api/v1/contacts/update` replaced the browser's Firestore write. Both
   * problems above are still fixed — more strongly, because they are now ENFORCED
   * rather than merely intended:
   *
   *   - The endpoint READS the document first and 404s when it is absent, so it cannot
   *     mint a phantom. That is the same semantic the old `update()` had, except the
   *     server also checks WHOSE book it is writing, against org membership resolved
   *     from `dashboard-orgs` — something a client-side rule could never do.
   *   - The prior state is read from the document, not passed in. `existing` is
   *     therefore no longer consulted here (see the parameter note below).
   *   - Every field is checked against a whitelist. A key that is Cloud-Function-derived
   *     or pipeline-owned is REJECTED rather than silently written, which is how the
   *     converter's `path` field stopped being persisted as a literal contact field —
   *     the old direct `update()` bypassed the converter that strips it.
   *
   * ── Optimistic, then reconciled ───────────────────────────────────────────────
   *
   * The in-memory array is patched BEFORE the request, so an edit shows instantly, and
   * the patch is ROLLED BACK if the request fails. That ordering is the whole reason
   * `patchContactInMemory` returns an undo: with the patch after the await there was
   * nothing to undo, but there was also nothing on screen for up to a second.
   *
   * **The reconciler is the document store, not this array.** The response carries two
   * read-backs: `contact`, the persisted row of the Postgres mirror (fleet-wide since
   * 2026-08-25, null only when the inline drain failed), which goes into the lookup store
   * below; and `doc` + `version`, the whole document as persisted, which
   * `useContactWrites` writes into `contactDocumentStore` — so the contact page and the
   * CRM side panel repaint from the true stored document in the response tick, including
   * the Cloud-Function-derived fields (`contactFullName`, `interestedIn*Distinct`) that no
   * client could have predicted. A change made ANYWHERE ELSE reaches them through the
   * contact's signal document (`useContactDocument`), or — while
   * `CONTACT_DOCUMENT_SOURCE` is not yet 'server' — through the Firestore listener on
   * the same path, which is writer-agnostic.
   *
   * Creating is `useContactWrites().createContact`; the two legacy tables' `updateContact`
   * picks between the two by looking the contact up in the book they hold.
   *
   * @param existing RETAINED FOR THE SIGNATURE, and unused. Six call sites pass the live
   *   document they are editing, and it was the prior state the legacy-mirror normalizers
   *   needed. The server derives those mirrors from the real document now, so the best
   *   prior state a caller can offer is strictly worse than the one the write already
   *   has. Kept rather than removed so this stays a drop-in change at every call site.
   */
  const updateExistingContact = async (
    contactId: string,
    data: any,
    existing: Partial<RealEstateContact> | null
  ): Promise<CrmContactUpdateResponse | void> => {
    if (!user?.email) throw new Error("Cannot update a contact while signed out");

    removeUndefined(data);

    const {
      // Server/Cloud-Function-owned stamps and the two derived in-memory fields — the
      // reserved keys the browser writers always stripped.
      _metadata,
      _ranking,
      createdTime,
      updatedTime,
      lastBulkMessageReceivedTime,
      // Deprecated free-text mirrors of `interestedInLocations`, which the endpoint
      // refuses: nothing derives them, so accepting them would let two representations
      // of the same fact drift with no reconciler. One legacy caller still sends
      // `neighborhood` alongside the locations it mirrors (the Matches tab's "update the
      // search profile" nudge), and a 400 there would block a save the realtor asked for
      // over a field they never typed. Dropped rather than forwarded, and warned about
      // rather than dropped silently — the fix is at the call site.
      street,
      neighborhood,
      ...dataToUpdate
    } = data;

    if (street !== undefined || neighborhood !== undefined) {
      console.warn(
        "updateExistingContact: dropped the deprecated street/neighborhood mirrors — " +
          "write interestedInLocations instead",
        { contactId }
      );
    }

    // Nothing to save is a legitimate outcome (a form saved untouched). It must not
    // become a request: the endpoint THROWS on an empty `fields` object rather than
    // treating it as a no-op, because a 200 on a patch that wrote nothing is
    // indistinguishable from a save that worked.
    if (Object.keys(dataToUpdate).length === 0) return;

    const rollback = patchContactInMemory(contactId, dataToUpdate);

    try {
      // Neither `ownerUserId` nor `view` is sent: this function's whole contract is the
      // SIGNED-IN USER'S OWN book, which is exactly what the endpoint defaults to.
      //
      // A COLLEAGUE's contact never comes through here. The endpoint does accept an
      // `ownerUserId` (intersected server-side with the caller's resolved scope), and that
      // branch lives in `useContactWrites().updateContact(id, fields, owner)`: a colleague's
      // book has no local copy in this browser, so there is nothing here to patch or roll
      // back. A caller that reaches this function with a colleague's record — the edit
      // DIALOG still can, it carries no owner — addresses its own book and gets the 404.
      const response = await contactsUpdateRef.current({ contactId, fields: dataToUpdate });
      // THE BLOCKED LIST CAN OVERRULE AN UNTICK (2026-09). `optedOutOfBulkMessages` is a mirror
      // of the personal blocked-list row; when the realtor unticks it against a row the flag did
      // not create (a human's הסר, a Meta verdict, a block placed on the blocked page) the server
      // keeps the row and sets the flag back to true. The optimistic patch above wrote `false`
      // into memory, so it is corrected here — the save itself succeeded, and the caller gets the
      // response so it can tell the realtor why the box is ticked again.
      if (response?.blockedList?.kept) {
        patchContactInMemory(contactId, { optedOutOfBulkMessages: true });
      }
      // The lookup store gets the row AS PERSISTED when the response carries it (which also
      // carries the overruled untick above), and the fields that were sent when it does not.
      if (response?.contact) {
        contactLookupStore.put(rowToContactSummary(response.contact));
      } else {
        contactLookupStore.patch(contactId, {
          ...pickSummaryFields(dataToUpdate),
          ...(response?.blockedList?.kept ? { optedOutOfBulkMessages: true } : {})
        });
      }
      return response;
    } catch (error) {
      rollback();
      // A server 404 IS "this contact is not in your book" — the same case the old
      // Firestore `not-found` mapped to, so every existing `isContactNotInYourBookError`
      // branch keeps working. Everything else is re-thrown with its own identity, so a
      // 400 from the field whitelist is not laundered into a friendlier but wrong error.
      throw toContactWriteError(error, contactId);
    }
  };

  return (
    <AppContext.Provider
      value={{
        allContacts,
        isLoadingContacts,
        contactsLoaded,
        updateExistingContact,
        rekeyContactInMemory,
        patchContactInMemory,
        upsertContactsInMemory,
        removeContactsInMemory,
        invalidateContactsBook,
        retainContactsLiveFeed,
        setAllContacts,
        getAllContacts
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export { AppContext, AppProvider };
